import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Button, Label, Input, Table } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import Header from "../../../layout/Header/Header";

function CajasTraspaso() {
  const { user } = useContext(AuthContext);
  const { id: idSalida } = useParams();

  const URL_TRASPASOS_SALIDA = process.env.REACT_APP_URL_TRASPASOS_SALIDA;
  const URL_ARTICULOS_TS = process.env.REACT_APP_URL_ARTICULOS_TS;
  const URL_CAJAS_TRASPASO = process.env.REACT_APP_URL_CAJAS_TRASPASO;

  const [traspaso, setTraspaso] = useState(null);
  const [itemsTraspaso, setItemsTraspaso] = useState([]); // artículos del traspaso (ArticulosTS)
  const [cajas, setCajas] = useState([]); // arreglo local editable de cajas
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!idSalida) return;
    const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

    // 1. Traer el traspaso
    axios.get(`${URL_TRASPASOS_SALIDA}/${idSalida}`, { headers })
      .then((res) => setTraspaso(res.data))
      .catch((err) => console.log(err));

    // 2. Traer los articulos del traspaso
    axios.get(`${URL_ARTICULOS_TS}/traspasoSalida/${idSalida}`, { headers })
      .then((res) => setItemsTraspaso(res.data || []))
      .catch((err) => console.log(err));

    // 3. Traer cajas existentes
    axios.get(`${URL_CAJAS_TRASPASO}ByTraspaso/${idSalida}`, { headers })
      .then((res) => {
        const existentes = (res.data || []).map((c) => ({
          _id: c._id,
          numeroCaja: c.numeroCaja,
          observaciones: c.observaciones || "",
          articulosCaja: (c.articulosCaja || []).map((a) => ({
            articuloId: a.articulos?.[0]?._id || a.articulos?.[0],
            SKU: a.articulos?.[0]?.SKU,
            nombre: a.articulos?.[0]?.nombre,
            cantidad: a.cantidad,
          })),
        }));
        if (existentes.length > 0) setCajas(existentes);
      })
      .catch((err) => console.log(err));
  }, [idSalida]);

  // Agrupa items del traspaso por articulo y calcula asignado/restante
  const resumenPorArticulo = () => {
    const map = new Map();
    for (const it of itemsTraspaso) {
      const art = it.articulos?.[0];
      if (!art) continue;
      const key = String(art._id);
      if (!map.has(key)) {
        map.set(key, {
          articuloId: art._id,
          SKU: art.SKU,
          nombre: art.nombre,
          total: 0,
        });
      }
      map.get(key).total += parseFloat(it.cantidad) || 0;
    }
    // Agregar asignado por caja
    const asignadoMap = new Map();
    for (const caja of cajas) {
      for (const a of caja.articulosCaja || []) {
        if (!a.articuloId) continue;
        const key = String(a.articuloId);
        asignadoMap.set(key, (asignadoMap.get(key) || 0) + (parseFloat(a.cantidad) || 0));
      }
    }
    return [...map.values()].map((r) => ({
      ...r,
      asignado: asignadoMap.get(String(r.articuloId)) || 0,
      restante: r.total - (asignadoMap.get(String(r.articuloId)) || 0),
    }));
  };

  const agregarCaja = () => {
    const nuevoNum = cajas.length > 0 ? Math.max(...cajas.map((c) => c.numeroCaja)) + 1 : 1;
    setCajas([...cajas, { numeroCaja: nuevoNum, observaciones: "", articulosCaja: [] }]);
  };

  const eliminarCaja = (idx) => {
    const copia = [...cajas];
    copia.splice(idx, 1);
    // Renumerar
    setCajas(copia.map((c, i) => ({ ...c, numeroCaja: i + 1 })));
  };

  const addItemToCaja = (cajaIdx, articuloId, SKU, nombre) => {
    const copia = [...cajas];
    const caja = copia[cajaIdx];
    if (caja.articulosCaja.some((a) => String(a.articuloId) === String(articuloId))) {
      Swal.fire({ icon: "warning", title: "Ya está en esta caja", text: "El artículo ya fue agregado a esta caja." });
      return;
    }
    caja.articulosCaja.push({ articuloId, SKU, nombre, cantidad: 0 });
    setCajas(copia);
  };

  const removeItemFromCaja = (cajaIdx, itemIdx) => {
    const copia = [...cajas];
    copia[cajaIdx].articulosCaja.splice(itemIdx, 1);
    setCajas(copia);
  };

  const updateItemCantidad = (cajaIdx, itemIdx, cantidad) => {
    const copia = [...cajas];
    copia[cajaIdx].articulosCaja[itemIdx].cantidad = cantidad;
    setCajas(copia);
  };

  const updateCajaObs = (cajaIdx, obs) => {
    const copia = [...cajas];
    copia[cajaIdx].observaciones = obs;
    setCajas(copia);
  };

  const guardar = async () => {
    // Validar: nada > restante inicial
    const totalPorArt = new Map();
    for (const it of itemsTraspaso) {
      const artId = String(it.articulos?.[0]?._id);
      totalPorArt.set(artId, (totalPorArt.get(artId) || 0) + (parseFloat(it.cantidad) || 0));
    }
    const asignadoPorArt = new Map();
    for (const c of cajas) {
      for (const a of c.articulosCaja || []) {
        const artId = String(a.articuloId);
        asignadoPorArt.set(artId, (asignadoPorArt.get(artId) || 0) + (parseFloat(a.cantidad) || 0));
      }
    }
    const excesos = [];
    for (const [artId, asignado] of asignadoPorArt) {
      const total = totalPorArt.get(artId) || 0;
      if (asignado > total) {
        excesos.push({ artId, asignado, total });
      }
    }
    if (excesos.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Cantidad excedida",
        text: `${excesos.length} artículo(s) tienen más asignado que el total del traspaso.`,
      });
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

      // Estrategia simple: borrar todas las cajas viejas y crear las nuevas
      await axios.delete(`${URL_CAJAS_TRASPASO}ByTraspaso/${idSalida}`, { headers });

      for (const caja of cajas) {
        await axios.post(URL_CAJAS_TRASPASO, {
          traspasosSalida: idSalida,
          numeroCaja: caja.numeroCaja,
          observaciones: caja.observaciones || "NA",
          articulosCaja: caja.articulosCaja.map((a) => ({
            articulos: [a.articuloId],
            cantidad: parseFloat(a.cantidad) || 0,
          })),
          user: user.id,
        }, { headers });
      }

      Swal.fire({ icon: "success", title: "Guardado", timer: 1200, showConfirmButton: false });
    } catch (err) {
      console.log(err);
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "No se pudo guardar" });
    } finally {
      setSaving(false);
    }
  };

  const imprimirEtiquetas = () => {
    if (!cajas.length) {
      Swal.fire({ icon: "warning", title: "Sin cajas", text: "Agrega al menos una caja." });
      return;
    }
    // PDF 20x15cm (200mm × 150mm) landscape — mismo tamano que las etiquetas de envio
    const ANCHO = 200;
    const ALTO = 150;
    const MARGEN = 10;
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: [ALTO, ANCHO] });
    const totalCajas = cajas.length;
    const areaOrigen = traspaso?.areaSalida?.[0]?.name || "-";
    const areaDestino = traspaso?.areaDestino?.[0]?.name || "-";
    const fecha = traspaso?.fecha || "";
    const idTraspaso = traspaso?.idTraspasosSalida || "";

    cajas.forEach((caja, idx) => {
      if (idx > 0) doc.addPage([ALTO, ANCHO], "landscape");

      // Header
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.text("TYDART — Etiqueta de Caja", MARGEN, 12);

      // Info principal
      doc.setFontSize(24);
      doc.setFont("helvetica", "bold");
      doc.text(`Caja ${caja.numeroCaja} de ${totalCajas}`, MARGEN, 26);

      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text(`Traspaso #${idTraspaso}   Fecha: ${fecha}`, MARGEN, 35);

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`${areaOrigen}  →  ${areaDestino}`, MARGEN, 45);

      // Tabla contenido
      const body = (caja.articulosCaja || []).map((a) => [a.SKU || "-", (a.nombre || "").substring(0, 45), a.cantidad]);
      const totalUnidades = (caja.articulosCaja || []).reduce((s, a) => s + (parseFloat(a.cantidad) || 0), 0);

      doc.autoTable({
        head: [["SKU", "Nombre", "Cant"]],
        body,
        startY: 50,
        margin: { left: MARGEN, right: MARGEN },
        styles: { fontSize: 10, cellPadding: 1.5 },
        headStyles: { fillColor: [30, 41, 59], fontSize: 10 },
        theme: "grid",
      });

      const finalY = doc.lastAutoTable?.finalY || 60;
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Total: ${totalUnidades} pzs`, MARGEN, Math.min(finalY + 8, ALTO - 12));

      if (caja.observaciones && caja.observaciones !== "NA") {
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        doc.text(`Nota: ${caja.observaciones.substring(0, 60)}`, MARGEN, ALTO - 5);
      }
    });

    doc.save(`Etiquetas_Traspaso_${idTraspaso}.pdf`);
  };

  const resumen = resumenPorArticulo();

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_traspasos_salida ? (
        <div className="card container col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/ListadoTraspasosSalida" color="danger">Regresar</Button>{" "}
              <Button size="sm" color="success" onClick={agregarCaja}>
                <i className="fas fa-plus"></i> Agregar Caja
              </Button>{" "}
              <Button size="sm" color="primary" onClick={guardar} disabled={saving}>
                {saving ? "Guardando..." : "Guardar Cajas"}
              </Button>{" "}
              <Button size="sm" color="warning" onClick={imprimirEtiquetas}>
                <i className="fas fa-print"></i> Imprimir Etiquetas
              </Button>
            </Col>
          </Row>
          <h3 align="center" style={{ marginTop: 10 }}>Cajas del Traspaso</h3>
          {traspaso && (
            <p style={{ textAlign: "center", color: "#64748b", fontSize: "0.85rem" }}>
              #{traspaso.idTraspasosSalida} · {traspaso.fecha} · {traspaso.areaSalida?.[0]?.name} → {traspaso.areaDestino?.[0]?.name}
            </p>
          )}

          {/* Resumen por articulo */}
          <div className="card" style={{ padding: 12, marginBottom: 12, background: "#f8fafc" }}>
            <Label className="negrita">Resumen del traspaso</Label>
            <Table size="sm" borderless>
              <thead>
                <tr>
                  <th>SKU</th><th>Nombre</th><th>Total</th><th>Asignado</th><th>Restante</th>
                </tr>
              </thead>
              <tbody>
                {resumen.map((r) => (
                  <tr key={r.articuloId} style={{ background: r.restante < 0 ? "#fee2e2" : r.restante === 0 ? "#dcfce7" : "transparent" }}>
                    <td>{r.SKU}</td>
                    <td>{r.nombre}</td>
                    <td>{r.total}</td>
                    <td>{r.asignado}</td>
                    <td style={{ fontWeight: 600 }}>{r.restante}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Cajas */}
          {cajas.length === 0 && (
            <p style={{ textAlign: "center", color: "#94a3b8" }}>Sin cajas. Click en "Agregar Caja" para empezar.</p>
          )}
          {cajas.map((caja, cajaIdx) => (
            <div key={cajaIdx} className="card" style={{ padding: 12, marginBottom: 12 }}>
              <Row>
                <Col md={2}>
                  <Label className="negrita">Caja {caja.numeroCaja}</Label>
                </Col>
                <Col md={8}>
                  <Input
                    bsSize="sm"
                    placeholder="Observaciones de la caja (ej. Frágil)"
                    value={caja.observaciones}
                    onChange={(e) => updateCajaObs(cajaIdx, e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Button size="sm" color="danger" onClick={() => eliminarCaja(cajaIdx)}>
                    <i className="fas fa-trash"></i> Eliminar caja
                  </Button>
                </Col>
              </Row>

              <Table size="sm" borderless className="mt-2">
                <thead>
                  <tr>
                    <th style={{ width: "20%" }}>SKU</th>
                    <th>Nombre</th>
                    <th style={{ width: "15%" }}>Cantidad</th>
                    <th style={{ width: "5%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {caja.articulosCaja.map((item, itemIdx) => (
                    <tr key={itemIdx}>
                      <td>{item.SKU}</td>
                      <td>{item.nombre}</td>
                      <td>
                        <Input
                          bsSize="sm"
                          type="number"
                          min="0"
                          value={item.cantidad}
                          onChange={(e) => updateItemCantidad(cajaIdx, itemIdx, e.target.value)}
                        />
                      </td>
                      <td>
                        <Button size="sm" color="link" onClick={() => removeItemFromCaja(cajaIdx, itemIdx)}>
                          <i className="fas fa-times"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={4}>
                      <Input
                        bsSize="sm"
                        type="select"
                        value=""
                        onChange={(e) => {
                          if (!e.target.value) return;
                          const art = resumen.find((r) => String(r.articuloId) === e.target.value);
                          if (art) addItemToCaja(cajaIdx, art.articuloId, art.SKU, art.nombre);
                          e.target.value = "";
                        }}
                      >
                        <option value="">+ Agregar artículo a esta caja...</option>
                        {resumen.map((r) => (
                          <option key={r.articuloId} value={r.articuloId}>
                            {r.SKU} — {r.nombre} (restante: {r.restante})
                          </option>
                        ))}
                      </Input>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          ))}
        </div>
      ) : undefined}
    </>
  );
}

export default CajasTraspaso;
